// Background step component - Step 1 of character creation wizard

import { css, html } from 'lit';
import { BaseComponent } from '../../lib/base-component.js';
import { ABILITY_NAMES, SKILL_ABILITIES } from '../../lib/constants.js';
import { renderMarkdown, slugToTitle } from '../../lib/format.js';
import { renderEquipmentPackages } from '../../lib/render-helpers.js';
import './option-card.js';
import './skill-option-card.js';
import './feat-choice-picker.js';

/**
 * @typedef {import('../views/create-character-view.js').BackgroundListItem} BackgroundListItem
 * @typedef {import('../views/create-character-view.js').BackgroundCreationOptions} BackgroundCreationOptions
 * @typedef {import('../views/create-character-view.js').FeatCreationChoices} FeatCreationChoices
 * @typedef {import('../views/create-character-view.js').EquipmentPackage} EquipmentPackage
 * @typedef {import('../../types/jsdoc-types.js').ChoiceOption} ChoiceOption
 */

/**
 * Background selection step component.
 * Controlled component: receives all data as properties, emits events for mutations.
 *
 * Events: select-background, select-equipment-package, select-tool,
 * select-feat-choice, toggle-feat-spell, toggle-replacement-skill
 */
class BackgroundStep extends BaseComponent {
  static properties = {
    backgrounds: { type: Array },
    selectedSlug: { type: String, attribute: 'selected-slug' },
    backgroundOptions: { type: Object },
    featOptions: { type: Object },
    backgroundEquipmentPackageId: { type: String },
    backgroundToolChoice: { type: String },
    featChoices: { type: Object },
    replacementSkills: { type: Array },
    classSkillProficiencies: { type: Array },
    classRecommendedBackground: { type: String, attribute: 'class-recommended-background' },
    alreadyChosenSpells: { type: Object },
    originMagicInitiateSpellList: { type: String, attribute: 'origin-magic-initiate-spell-list' },
    featRecommendedSlugs: { type: Object },
    prefilledFeatChoices: { type: Object },
    duplicateSkills: { type: Array },
    availableReplacementSkills: { type: Array },
    isHeaderStuck: { type: Boolean, attribute: 'is-header-stuck' },
    // internal
    _expandedBackgrounds: { type: Object, state: true },
  };

  static styles = css`
    :host {
      display: block;
    }

    .selectable-card {
      cursor: pointer;
      transition:
        border-color 0.15s,
        box-shadow 0.15s;
    }

    .selectable-card:hover:not(.disabled) {
      border-color: #0d6efd;
    }

    .selectable-card.selected {
      border-color: #0d6efd;
      box-shadow: 0 0 0 1px #0d6efd;
      background-color: rgba(13, 110, 253, 0.04);
    }

    .sticky-header {
      position: sticky;
      top: 0;
      background-color: rgba(255, 255, 255, 0.5);
      backdrop-filter: blur(8px);
      z-index: 10;
      padding-top: 0.5rem;
      padding-bottom: 1rem;
      margin-top: -0.5rem;
      margin-left: -1rem;
      margin-right: -1rem;
      padding-left: 1rem;
      padding-right: 1rem;
      border-bottom: 1px solid transparent;
      transition: border-color 0.15s ease;
    }

    .sticky-header.stuck {
      border-bottom-color: rgba(0, 0, 0, 0.125);
      padding-top: 1rem;
    }

    .option-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
      gap: 0.5rem;
    }
  `;

  constructor() {
    super();
    /** @type {Array<BackgroundListItem>} */
    this.backgrounds = [];
    this.selectedSlug = '';
    /** @type {BackgroundCreationOptions | null} */
    this.backgroundOptions = null;
    /** @type {FeatCreationChoices | null} */
    this.featOptions = null;
    this.backgroundEquipmentPackageId = '';
    this.backgroundToolChoice = '';
    /** @type {{[key: string]: string}} */
    this.featChoices = {};
    /** @type {Array<string>} */
    this.replacementSkills = [];
    /** @type {Array<string>} */
    this.classSkillProficiencies = [];
    this.classRecommendedBackground = '';
    /** @type {Set<string>} */
    this.alreadyChosenSpells = new Set();
    this.originMagicInitiateSpellList = '';
    /** @type {{[key: string]: string}} */
    this.featRecommendedSlugs = {};
    /** @type {{[key: string]: string}} */
    this.prefilledFeatChoices = {};
    /** @type {Array<string>} */
    this.duplicateSkills = [];
    /** @type {Array<string>} */
    this.availableReplacementSkills = [];
    this.isHeaderStuck = false;
    /** @type {{[key: string]: boolean}} */
    this._expandedBackgrounds = {};
  }

  get _needsSkillReplacement() {
    return this.duplicateSkills.length > 0;
  }

  render() {
    return html`
      <div>
        <div class="sticky-header ${this.isHeaderStuck ? 'stuck' : ''}">
          <h5 class="card-title mb-0">Choose Your Background</h5>
        </div>
        <p class="text-muted mb-3">
          Your background describes where you came from and what you did before becoming an adventurer.
        </p>

        <div class="option-grid mb-4">
          ${this.backgrounds.map((bg) => {
            const isExpanded = this._expandedBackgrounds[bg.slug];
            const firstSentence = `${bg.description?.split('. ')[0]}.`;
            const hasMore = bg.description && bg.description !== firstSentence;
            const isRecommended = this.classRecommendedBackground === bg.slug;
            return html`
              <div
                class="card h-100 selectable-card ${this.selectedSlug === bg.slug ? 'selected' : ''}"
                @click="${() => this._onSelectBackground(bg.slug)}"
              >
                <div class="card-body">
                  <h6 class="card-title mb-1 ${isRecommended ? 'd-flex align-items-center' : ''}">
                    ${bg.name}${
                      isRecommended
                        ? html` <span class="badge bg-primary bg-opacity-25 text-primary ms-2">Recommended</span>`
                        : ''
                    }
                  </h6>
                  <p class="card-text small text-muted mb-2">
                    ${isExpanded ? bg.description : firstSentence}
                    ${
                      hasMore
                        ? html`
                          <a
                            href="#"
                            class="ms-1"
                            @click="${(/** @type {Event} */ e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              this._expandedBackgrounds = { ...this._expandedBackgrounds, [bg.slug]: !isExpanded };
                            }}"
                            >${isExpanded ? 'Less' : 'More'}</a
                          >
                        `
                        : ''
                    }
                  </p>
                  <div class="small text-muted fst-italic">
                    Ability Scores:
                    ${bg.abilityScores
                      ?.map((/** @type {string} */ a) => /** @type {Record<string, string>} */ (ABILITY_NAMES)[a] || a)
                      .join(', ')}
                  </div>
                </div>
              </div>
            `;
          })}
        </div>

        ${this.backgroundOptions ? this._renderBackgroundOptions() : ''}
      </div>
    `;
  }

  _renderBackgroundOptions() {
    const options = this.backgroundOptions;
    if (!options) return html``;
    return html`
      <!-- Skill Proficiencies -->
      ${
        (options.skills?.skills?.length ?? 0) > 0
          ? html`
            <div class="mb-4">
              <h6 class="mb-2">Skill Proficiencies</h6>
              <div class="row g-2 mb-2">
                ${options.skills?.skills?.map((/** @type {string} */ skill) => {
                  const isDuplicate = this.classSkillProficiencies.includes(skill);
                  const label = slugToTitle(skill) + (isDuplicate ? ' (Overlap)' : '');
                  return html`
                    <div class="col-auto">
                      <option-card
                        name="${label}"
                        slug="${skill}"
                        ?selected="${true}"
                        ?disabled="${true}"
                        input-type="checkbox"
                      ></option-card>
                    </div>
                  `;
                })}
              </div>
              ${
                this._needsSkillReplacement
                  ? html`
                    <div class="alert alert-warning py-2 mb-2">
                      <small
                        >You already have ${this.duplicateSkills.map((s) => slugToTitle(s)).join(', ')} from your class.
                        Choose ${this.duplicateSkills.length} replacement
                        skill${this.duplicateSkills.length > 1 ? 's' : ''}.</small
                      >
                      <span class="badge bg-primary ms-2"
                        >${this.replacementSkills.length} / ${this.duplicateSkills.length}</span
                      >
                    </div>
                    <div class="option-grid">
                      ${this.availableReplacementSkills.map((skill) => {
                        const isClassSkill = this.classSkillProficiencies.includes(skill);
                        const isSelected = this.replacementSkills.includes(skill);
                        const isDisabled =
                          isClassSkill || (!isSelected && this.replacementSkills.length >= this.duplicateSkills.length);
                        return html`
                          <skill-option-card
                            name="${slugToTitle(skill)}"
                            slug="${skill}"
                            ability="${SKILL_ABILITIES[skill] || ''}"
                            ?selected="${isSelected || isClassSkill}"
                            ?disabled="${isDisabled}"
                            ?known="${isClassSkill}"
                            @card-selected="${(/** @type {CustomEvent} */ e) =>
                              !isDisabled && this._onToggleReplacementSkill(e.detail.slug)}"
                          ></skill-option-card>
                        `;
                      })}
                    </div>
                  `
                  : ''
              }
            </div>
          `
          : ''
      }

      <!-- Equipment Packages -->
      ${
        (options.equipmentPackages?.length ?? 0) > 0
          ? renderEquipmentPackages(
              /** @type {Array<EquipmentPackage>} */ (options.equipmentPackages),
              this.backgroundEquipmentPackageId,
              'bg-equipment-package',
              (slug) => this._onSelectEquipmentPackage(slug),
              (desc) =>
                desc.replace(
                  /\{toolChoice\}/g,
                  this.backgroundToolChoice ? slugToTitle(this.backgroundToolChoice) : 'Gaming Set',
                ),
            )
          : ''
      }

      <!-- Tool Proficiency -->
      ${
        options.tools?.type === 'choice'
          ? html`
            <div class="mb-4">
              <h6 class="mb-2 d-inline">Tool Proficiency</h6>
              <span class="badge bg-primary ms-1">${this.backgroundToolChoice ? 1 : 0} / 1</span>
              <div class="row g-2 mt-2">
                ${(options.tools.options ?? []).map(
                  (/** @type {string} */ tool) => html`
                    <div class="col-auto">
                      <option-card
                        name="${slugToTitle(tool)}"
                        slug="${tool}"
                        ?selected="${this.backgroundToolChoice === tool}"
                        input-type="radio"
                        radio-group="tool-choice"
                        @card-selected="${() => this._onSelectTool(tool)}"
                      ></option-card>
                    </div>
                  `,
                )}
              </div>
            </div>
          `
          : options.tools?.type === 'fixed'
            ? html`
              <div class="mb-4">
                <h6 class="mb-2">Tool Proficiency</h6>
                <div class="row g-2">
                  ${(options.tools.tools ?? []).map(
                    (/** @type {string} */ tool) => html`
                      <div class="col-auto">
                        <option-card
                          name="${slugToTitle(tool)}"
                          slug="${tool}"
                          ?selected="${true}"
                          ?disabled="${true}"
                          input-type="checkbox"
                        ></option-card>
                      </div>
                    `,
                  )}
                </div>
              </div>
            `
            : ''
      }

      <!-- Origin Feat -->
      ${
        options.feat?.slug
          ? html`
            <div class="mb-4">
              <h6 class="mb-2">
                Origin Feat: ${this.featOptions?.feat?.name || options.feat.name || slugToTitle(options.feat.slug)}
              </h6>
              ${
                this.featOptions?.feat?.description
                  ? html`<div class="text-muted small mb-3">${renderMarkdown(this.featOptions.feat.description)}</div>`
                  : ''
              }
              ${
                this.featOptions?.choices
                  ? html`
                    <feat-choice-picker
                      .choices="${this.featOptions.choices}"
                      .selections="${this.featChoices}"
                      .alreadyChosenSpells="${this.alreadyChosenSpells}"
                      excluded-spell-list="${this.originMagicInitiateSpellList || ''}"
                      .recommendedSlugs="${this.featRecommendedSlugs}"
                      .prefilledChoices="${this.prefilledFeatChoices}"
                      radio-prefix="feat"
                      @choice-selected="${(/** @type {CustomEvent} */ e) =>
                        this._onSelectFeatChoice(e.detail.choiceId, e.detail.value)}"
                      @spell-toggled="${(/** @type {CustomEvent} */ e) =>
                        this._onToggleFeatSpell(e.detail.choiceId, e.detail.spellSlug)}"
                    ></feat-choice-picker>
                  `
                  : ''
              }
            </div>
          `
          : ''
      }
    `;
  }

  /** @param {string} slug */
  _onSelectBackground(slug) {
    this.dispatchEvent(new CustomEvent('select-background', { detail: { slug }, bubbles: true, composed: true }));
  }

  /** @param {string} slug */
  _onSelectEquipmentPackage(slug) {
    this.dispatchEvent(
      new CustomEvent('select-equipment-package', { detail: { slug }, bubbles: true, composed: true }),
    );
  }

  /** @param {string} tool */
  _onSelectTool(tool) {
    this.dispatchEvent(new CustomEvent('select-tool', { detail: { tool }, bubbles: true, composed: true }));
  }

  /**
   * @param {string} choiceId
   * @param {string} value
   */
  _onSelectFeatChoice(choiceId, value) {
    this.dispatchEvent(
      new CustomEvent('select-feat-choice', { detail: { choiceId, value }, bubbles: true, composed: true }),
    );
  }

  /**
   * @param {string} choiceId
   * @param {string} spellSlug
   */
  _onToggleFeatSpell(choiceId, spellSlug) {
    this.dispatchEvent(
      new CustomEvent('toggle-feat-spell', { detail: { choiceId, spellSlug }, bubbles: true, composed: true }),
    );
  }

  /** @param {string} slug */
  _onToggleReplacementSkill(slug) {
    this.dispatchEvent(
      new CustomEvent('toggle-replacement-skill', { detail: { slug }, bubbles: true, composed: true }),
    );
  }
}

customElements.define('background-step', BackgroundStep);
